import { TafaceCertificaDurationEnum } from '../types/enums/taface-certificate-duration.enum';
import { TafaceTypePlanEnum } from '../types/enums/taface-type-plan.enum';
import { hasValue } from './objectUtils';

const TAFACE_DURATION_STRINGS = [
  TafaceCertificaDurationEnum['1 año'],
  TafaceCertificaDurationEnum['2 años'],
] as const;

export function normalizeCertificateDurationFromApi(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    if (TAFACE_DURATION_STRINGS.includes(value as (typeof TAFACE_DURATION_STRINGS)[number])) return value;
    const t = value.trim().toLowerCase();
    if (t.includes('2')) return TafaceCertificaDurationEnum['2 años'];
    if (t.includes('1')) return TafaceCertificaDurationEnum['1 año'];
  }
  if (value === 1) return TafaceCertificaDurationEnum['1 año'];
  if (value === 2) return TafaceCertificaDurationEnum['2 años'];
  return null;
}

const PLAN_TYPE_STRINGS = [TafaceTypePlanEnum.LITE, TafaceTypePlanEnum.PREMIUM] as const;

export function normalizePlanTypeFromApi(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return null;
    if (PLAN_TYPE_STRINGS.includes(t as (typeof PLAN_TYPE_STRINGS)[number])) return t;
    const u = t.toUpperCase();
    if (u === TafaceTypePlanEnum.LITE) return TafaceTypePlanEnum.LITE;
    if (u === TafaceTypePlanEnum.PREMIUM) return TafaceTypePlanEnum.PREMIUM;
  }
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (n === 0) return TafaceTypePlanEnum.LITE;
  if (n === 5) return TafaceTypePlanEnum.PREMIUM;
  return null;
}

export function pickPlanTypeFromElectronicInvoice(
  source: Record<string, unknown> | null | undefined,
): unknown {
  if (!source || typeof source !== 'object') return undefined;
  if (source.planType !== undefined) return source.planType;
  if (source.PlanType !== undefined) return source.PlanType;
  if (source.plan_type !== undefined) return source.plan_type;
  const nested = source.electronicInvoice ?? source.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    if (inner.planType !== undefined) return inner.planType;
    if (inner.PlanType !== undefined) return inner.PlanType;
    if (inner.plan_type !== undefined) return inner.plan_type;
  }
  return undefined;
}

export function mapElectronicInvoiceApiToForm(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (out[k] === undefined || !hasValue(out[k])) {
      out[k] = v;
    }
  }
  return out;
}

export function electronicInvoicePayloadHasAnyValue(normalized: Record<string, unknown>): boolean {
  return Object.values(normalized).some((v) => hasValue(v));
}

const CERTIFICATE_REGISTRATION_FIELD_KEYS = [
  'dgiCode',
  'businessType',
  'companyType',
  'certificateDuration',
  'contactEmail',
  'documentType',
  'documentNumber',
  'firstName',
  'secondName',
  'firstLastName',
  'secondLastName',
  'phone',
  'applicantAddress',
  'applicantCity',
  'applicantDepartment',
] as const;

export function emptyCertificateRegistrationFields(): Record<string, unknown> {
  return {
    dgiCode: '',
    businessType: '',
    companyType: '',
    certificateDuration: '',
    contactEmail: '',
    documentType: null,
    documentNumber: '',
    firstName: '',
    secondName: '',
    firstLastName: '',
    secondLastName: '',
    phone: '',
    applicantAddress: '',
    applicantCity: '',
    applicantDepartment: '',
  };
}

export function nullifyCertificateRegistrationFieldsInPayload(
  payload: Record<string, unknown>,
  digitalCertificateRequest: boolean,
): Record<string, unknown> {
  if (digitalCertificateRequest) return payload;
  const out = { ...payload };
  for (const key of CERTIFICATE_REGISTRATION_FIELD_KEYS) {
    out[key] = null;
  }
  return out;
}

export function clearElectronicInvoiceCertificateBlockFields(
  setFieldValue: (field: string, value: unknown) => void,
) {
  const empty = emptyCertificateRegistrationFields();
  for (const key of CERTIFICATE_REGISTRATION_FIELD_KEYS) {
    setFieldValue(key, empty[key]);
  }
}

export function restoreElectronicInvoiceCertificateBlockFromApi(
  api: Record<string, unknown> | null,
  setFieldValue: (field: string, value: unknown) => void,
) {
  if (!api) return;
  for (const key of CERTIFICATE_REGISTRATION_FIELD_KEYS) {
    let v = api[key];
    if (key === 'certificateDuration') {
      v = normalizeCertificateDurationFromApi(v);
    }
    setFieldValue(key, v === undefined ? null : v);
  }
}
